import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";
import { providerIdZodSchema, type ProviderId } from "../providers/provider-id";
import {
    storageItemImageIdZodSchema,
    type StorageItemImageId,
} from "../storage-items/storage-item-images/storage-item-image-id";
import { resourceMapIdZodSchema, type ResourceMapId } from "./resource-map-id";

type ResourceMapAddCommand = {
    readonly _id: string;
    readonly _type: "RESOURCE_MAP_ADD_COMMAND";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly data: {
        _id?: ResourceMapId;
        title: string;
        storageItemId: StorageItemImageId;
        ownerId: ProviderId;
        /**
         * Figma can't export class names to svg, but it can add them with dotnotation to id of the svg element.
         * This is a fix for that.
         *
         * When this is true, we will look for `.selectable"`, `.color_switching_area"`, `.text_element"`
         * and replace them with `" class="selectable"`, `" class="color_switching_area"` and `" class="text_element"`.
         * @type {boolean}
         */
        figmaSvgExportFix?: boolean;
    };
};

type FactoryParams = ResourceMapAddCommand["data"];
const resourceMapAddCommandFactory = (params: FactoryParams) => {
    const ResourceMapAddCommand: ResourceMapAddCommand = {
        _id: generateId({ prefix: "resource_map_add_command__" }),
        _type: "RESOURCE_MAP_ADD_COMMAND",
        _createdAt: nowIsoAndUnix(),
        data: params,
    };
    return ResourceMapAddCommand;
};

const resourceMapAddCommandFactoryZodSchema: z.ZodSchema<FactoryParams> =
    z.object({
        _id: resourceMapIdZodSchema.optional(),
        title: z.string().min(1),
        storageItemId: storageItemImageIdZodSchema,
        ownerId: providerIdZodSchema,
    });

const resourceMapAddCommandZodSchema: z.ZodSchema<ResourceMapAddCommand> =
    z.object({
        _id: idZodSchema,
        _type: z.literal("RESOURCE_MAP_ADD_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        data: resourceMapAddCommandFactoryZodSchema,
    });

export {
    resourceMapAddCommandFactory,
    resourceMapAddCommandFactoryZodSchema,
    resourceMapAddCommandZodSchema,
};
export type {
    ResourceMapAddCommand,
    FactoryParams as ResourceMapAddCommandFactoryParams,
};
